import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Keyboard } from '@capacitor/keyboard';
import { Platform, IonIcon, IonItem, IonLabel, IonList, IonRadioGroup, IonRadio, IonButton, IonCheckbox } from '@ionic/angular/standalone';

import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { UserStripePaymentsApiService, UserBookingsApiService, UserFreeHoursPaymentsApiService, UserTowerGroupsApiService } from 'src/app/api/services';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PayWithWrapperComponent } from '../pay-with-wrapper/pay-with-wrapper.component';
import { TranslatePipe } from '@ngx-translate/core';
import { addIcons } from "ionicons";
import { closeCircleOutline, removeOutline, addOutline } from "ionicons/icons";
import { CustomerPaymentMethod, PaymentInfo, PaymentMethodEnum } from 'src/shared/models/payment.model';
import { BookingHelperService } from 'src/shared/services/helpers/booking-helper.service';
import { CustomSpinnerService } from 'src/shared/services/helpers/custom-spinner.service';
import { EventsService } from 'src/shared/services/helpers/events.service';
import { PaymentsHelperService } from 'src/shared/services/helpers/payments-helper.service';
import { BookingPaymentInformation } from 'src/shared/components/what-to-pay-block/booking-payment-information.component';

@Component({
    selector: 'vl-choose-free-hours',
    templateUrl: './choose-free-hours.component.html',
    styleUrls: ['./choose-free-hours.component.scss'],
    imports: [IonCheckbox, IonButton, IonRadio, IonRadioGroup, IonList, IonLabel, IonItem, IonIcon, BookingPaymentInformation, NgIf, FormsModule, NgFor, PayWithWrapperComponent, TitleCasePipe, TranslatePipe]
})
export class ChooseFreeHoursComponent implements OnInit {
    @ViewChild('giropayInput', { static: false }) giropayInput: ElementRef;
    @Input() creditCards: Array<CustomerPaymentMethod>;
    @Input() toPayCardInfo: PaymentInfo;
    @Input() fromBuySubscription: boolean = false;
    @Input() showBackBtn = true;
    @Input() type: 'select' | 'choose' = 'select';
    @Output() next: EventEmitter<any> = new EventEmitter<any>();
    @Output() terminated: EventEmitter<any> = new EventEmitter<any>();
    @Output() onSuccess: EventEmitter<any> = new EventEmitter<any>();
    @Output() onError: EventEmitter<any> = new EventEmitter<any>();
    creditCardsForDisplay: Array<CustomerPaymentMethod> = [];
    paymentEnum = PaymentMethodEnum;
    useFreeHours = false;
    freeHours = 0;
    amount;
    paymentType: { type: 'new-card' | 'saved-card', card: CustomerPaymentMethod, returnSlide?: number };
    giropayFullName: string = '';
    giropayValidationFailed: boolean = false;
    defaultPaymentMethod = null;
    stripeDisabled: boolean = false;
    hidePayButtons: boolean = false;
    stripeSupported: boolean;
    selectedCard: any = this.defaultPaymentMethod;
    stripeCardOption = { type: this.paymentEnum.STRIPE_CARD, id: null };

    constructor(private bookingHelper: BookingHelperService,
        private spinner: CustomSpinnerService,
        private eventsService: EventsService,
        private cd: ChangeDetectorRef,
        private router: Router,
        private platform: Platform,
        private groupsService: UserTowerGroupsApiService,
        private paymentsHelper: PaymentsHelperService,
        private stripePaymentService: UserStripePaymentsApiService,
        private freeHoursService: UserFreeHoursPaymentsApiService,
        private bookingService: UserBookingsApiService) {
        addIcons({ closeCircleOutline, removeOutline, addOutline });
    }

    ngOnInit() {
        if (this.type === 'select') {
            this.creditCardsForDisplay = this.creditCards;
        }
        this.setDefaultMethod();

        Keyboard.addListener('keyboardWillShow', info => {
            this.hidePayButtons = true;
            this.cd.detectChanges();
        });
        Keyboard.addListener('keyboardDidShow', info => {
            if (this.platform.is('ios')) document.activeElement.scrollIntoView(true);
            this.cd.detectChanges();
        });

        Keyboard.addListener('keyboardWillHide', () => {
            this.hidePayButtons = false;
            this.cd.detectChanges();
        });
        this.stripeSupported = this.isStripeSupported();
    }
    displayFreeHours() {
        return this.toPayCardInfo.type !== 'subscription' && (this.toPayCardInfo.freeHours > 0) && this.isPaymentMethodSupported(PaymentMethodEnum.FREE_HOURS);
    }

    isStripeSupported() {
        return this.isPaymentMethodSupported(PaymentMethodEnum.STRIPE_CARD);
    }

    private isPaymentMethodSupported(method: PaymentMethodEnum): boolean {
        return this.paymentsHelper.isPaymentMethodSupported(method);
    }

    openSupport() {
        this.groupsService.getUserTowerGroups().pipe(take(1)).subscribe(g => {
            const group = g?.find(v => v.id == this.toPayCardInfo.groupId);
            this.bookingHelper.openSupport(group || null);
        });
    }

    setDefaultMethod() {
        this.stripeDisabled = this.toPayCardInfo.amountToPay < 0.5;
        this.defaultPaymentMethod = this.stripeDisabled ? this.creditCardsForDisplay.find(v => v.type === 'PAYPAL') : this.creditCardsForDisplay[0];
        this.paymentType = {
            card: this.defaultPaymentMethod,
            type: 'saved-card'
        }
        this.cd.detectChanges();
    }

    onFreeHoursChange(checked: boolean) {
        this.useFreeHours = checked;
        this.checkboxChanged();
    }      

    pay() {
        this.bookingHelper.towerReadyToAction(this.toPayCardInfo.type, (valid) => {
            if (valid) {
                this.bookingService.getAmount({
                    bookingId: this.toPayCardInfo.bookingId,
                    freeHours: Number(this.freeHours)
                }).subscribe(r => {
                    if (r.amount === 0) {
                        this.terminateBooking();
                    }
                })
            }
        }, this.toPayCardInfo.bookingId, this.toPayCardInfo.groupId, this.toPayCardInfo.subscriptionStartDate);
    }

	private addMethodsForChoose() {
		// let giropay = this.creditCards.find(cc => cc.type === PaymentMethodEnum.STRIPE_GIROPAY);
		let paypal = this.creditCards.find(cc => cc.type === PaymentMethodEnum.PAYPAL);

		if (!paypal) {
			this.creditCardsForDisplay.push(this.paymentsHelper.createMethod(PaymentMethodEnum.PAYPAL));
		}
		// if (!giropay && this.toPayCardInfo.currency === 'EUR' && Number(this.toPayCardInfo.amountToPay) >= 1) {
		// 	this.creditCardsForDisplay.push(this.paymentsHelper.createMethod(PaymentMethodEnum.STRIPE_GIROPAY));
		// }

	}

	async back() {
		await this.paymentsHelper.backToBooking(this.toPayCardInfo);
	}

    checkboxChanged() {
        setTimeout(() => {
            if (!this.useFreeHours) {
                this.freeHours = 0;
            } else {
                this.freeHours = this.getFreeHours();
            }
            this.calc();
            this.cd.detectChanges();
        }, 100)
    }

    getFreeHours() {
        let freeHours;
        if (this.toPayCardInfo.freeHours >= this.toPayCardInfo.recommendedFreeHours) {
            freeHours = this.toPayCardInfo.recommendedFreeHours;
        } else {
            freeHours = 0;
        }
        return freeHours;
    }

    add() {
        let b = this.toPayCardInfo.freeHours <= this.freeHours;
        let b1 = !(this.freeHours < this.toPayCardInfo.recommendedFreeHours);
        if (b === true || b1 === true) {
            return;
        }
        this.freeHours++;
        this.calc();
    }

    minus() {
        if (this.freeHours) {
            this.freeHours--;
            this.calc();
        } else {
            this.cd.detectChanges();
        }
    }

    longPress() {
        console.log('long press');
    }

    async calc() {
        await this.spinner.show();
        const amountDto = await firstValueFrom(this.bookingService.getAmount({ bookingId: this.toPayCardInfo.bookingId, freeHours: this.freeHours }));
        this.freeHours = amountDto.usedFreeHours;
        this.toPayCardInfo.recommendedFreeHours = amountDto.recommendedFreeHours;
        this.toPayCardInfo.freeHoursUsed = amountDto.usedFreeHours;
        this.toPayCardInfo.amountToPay = amountDto.amount.toFixed(2);
        this.toPayCardInfo.duration = this.bookingHelper.calcTime(moment.duration(amountDto.bookingBreakdown.bookingDuration, 'minutes'), false);
        const extraDuration = moment.duration(amountDto.bookingBreakdown.bookingDurationToPay, 'minutes').subtract(moment.duration(amountDto.usedFreeHours, 'hours'));
        this.toPayCardInfo.extraDuration = extraDuration.asMinutes() > 0 ? this.bookingHelper.calcTime(extraDuration, false) : '0';
        this.stripeDisabled = this.toPayCardInfo.amountToPay < 0.5 && (this.toPayCardInfo.amountToPay > 0);
        this.cd.detectChanges();
        await this.spinner.hide();
    }

    compareCards = (a: any, b: any): boolean => {
        if (!a || !b) return false;
      
        const isNewCard = a.type === this.paymentEnum.STRIPE_CARD && !a.id;
        const isNewCardOther = b.type === this.paymentEnum.STRIPE_CARD && !b.id;
      
        if (isNewCard && isNewCardOther) return true;
      
        if (isNewCard || isNewCardOther) return false;
      
        return a.type === b.type && a.id === b.id;
    };

    chooseCard(card: CustomerPaymentMethod, type: 'new-card' | 'saved-card' = 'saved-card') {
        this.defaultPaymentMethod = card;
        this.paymentType = { card, type };
        this.giropayValidationFailed = false;
    }

    terminateBooking() {
        this.spinner.show().then(() => {
            this.freeHoursService.createPayment({
                body: {
                    bookingId: Number(this.toPayCardInfo.bookingId),
                    freeHours: Number(this.freeHours)
                }
            }).pipe(
                mergeMap(p => {
                    return this.stripePaymentService.finishPayment({
                        body: {
                            freeHoursPaymentId: p.paymentId
                        }
                    })
                }),
                mergeMap(() => {
                    return this.bookingService.terminateBooking({
                        bookingId: Number(this.toPayCardInfo.bookingId),
                    })
                })
            ).subscribe(() => {
                this.eventsService.refreshBookings();
                this.bookingHelper.handleTerminateBooking(this.toPayCardInfo.booking.id);
                this.terminated.emit();
                this.spinner.hide();
            });
        });
    }

    goHome() {
        this.router.navigate(['/home'], { replaceUrl: true });
    }

    inputChange(event) {
        this.giropayValidationFailed = false;
        this.cd.detectChanges();
    }

    onValidationError() {
        this.giropayValidationFailed = true;
        this.giropayInput.nativeElement.focus();
        this.cd.detectChanges();
    }
}